import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";

import OrderInfo from "./OrderInfo";

import FeeDetail from "./FeeDetail";
import { useTranslation } from "react-i18next";

import { SvgIcon } from "@/components/SvgIcon";

import {
  allInPayCard,
  allInPayQrcode,
  commitOrder,
  createPodOrder,
  wxPay,
} from "@/api/order";

import { message, Form, Button, Modal, Divider, Checkbox } from "antd";

import { getUser } from "@/api/config";
import {
  allInPayTypeMap,
  InvoiceTypeMap,
  PayWay,
  SOURCE_ORDER_TYPE,
  OrderTypeToInvoiceContentMap,
} from "@/utils/contants";
import AuthOtherOrder from "@/components/Order/AuthOtherOrder";
import BuyJournal from "@/components/Order/BuyJournal";
import {
  OrderStore,
  mergeOrderListStore,
  mergeOrderSelectIdsStore,
  podBillStore,
  buyJournalStore,
  isBuyJournalSelector,
} from "@/store/order";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import WxPay from "@/components/PayMask/WxPay";

import style from "./pc.module.scss";
import { math } from "@/utils/math";
import { useNavigate } from "react-router";
import { IS_MOBILE, toLogin } from "@/utils/tool";
import * as Sentry from "@sentry/react";
import InvoiceModal from "@/components/Modal/Invoice";
import sentry4Action from "@/utils/sentry";
import OfflineModal from "@/components/Modal/Offline";

type Props = {
  order: Order;
};

type TotalInfo = Pick<
  Order,
  | "total_money"
  | "exp_cny_pay_money"
  | "tax_money"
  | "commission_money"
  | "currency"
  | "raw_exp_money"
  | "pay_methods"
  | "pay_status"
  | "order_id"
  | "configuration"
>;

const PcInfo = (props: Props) => {
  const { order } = props;
  const { t } = useTranslation();
  const [protocol, setProtocol] = useState(false);
  const [way, setWay] = useState<PayWay>(order.pay_methods[0]);
  const user = getUser();
  const [showMerge, setShowMerge] = useState(false);
  const mergeOrderList = useRecoilValue(mergeOrderListStore);
  const mergeOrderSelectIds = useRecoilValue(mergeOrderSelectIdsStore);
  // apc定购期刊数量
  const [podBillState] = useRecoilState(podBillStore);
  const isBuyCheckedStore = useRecoilValue(isBuyJournalSelector);
  const buyJournal = useRecoilValue(buyJournalStore);
  const setOrder = useSetRecoilState(OrderStore);
  const [moneyList, setMoneyList] = useState<Array<number>>();
  const [showWxPay, setShowWxPay] = useState(false);
  const [payUrl, setPayUrl] = useState("");
  const [transaction_id, setTransaction_id] = useState("");
  const [payLoading, setPayLoading] = useState(false);
  const [modalInstance, setModalInstance] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const navigate = useNavigate();
  // 将表单声明移到组件外部
  const [buyJournalForm] = Form.useForm();

  const [totalInfo, setTotalInfo] = useState<TotalInfo>({
    total_money: order.total_money,
    exp_cny_pay_money: order.exp_cny_pay_money,
    tax_money: order.tax_money,
    commission_money: order.commission_money,
    currency: order.currency,
    raw_exp_money: order.raw_exp_money,
    pay_methods: order.pay_methods,
    pay_status: order.pay_status,
    order_id: order.order_id,
    configuration: order.configuration,
  });

  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  const [bill, setBill] = useState<(InvoiceInfo & InvoiceAddress) | null>(null);

  const [podPayInfo, setPodPayInfo] = useState<{
    order_ids: string[];
    total_amounts: number[];
  }>();
  /**
   * 未填写情况下，如果pod填了，用pod的
   */
  const defaultBill = useMemo(() => {
    if (bill) return bill;
    if (!podBillState) return bill;
    const { bill_remark, bill_content, ...remainOrderBill } = podBillState;
    return {
      ...remainOrderBill,
      bill_remark: "",
      bill_content: OrderTypeToInvoiceContentMap[order.order_type],
    };
  }, [bill, podBillState]);

  /**
   * 确认支付。选中银行卡时走通联银行卡，其余沿用当前选中的支付方式。
   */
  const confirmPay = async () => {
    if (!user) {
      toLogin();
      return;
    }
    if (!protocol) {
      messageApi.info(t("请勾选“我已阅读 PubLink 使用协议”"));
      return;
    }
    sentry4Action("确认支付", {
      order_id: order.order_id,
      order_type: order.expense_type,
      publishing_house_name: order.publishing_house_name,
      journal_name: order.journal_name,
      article_title: order.article_title,
      cae: order.contact_author_email.split("@").join(" "),
      meeting_name: order?.meeting_name,
    });

    const podOrderData = {
      orderIds: [] as string[],
      totalAmounts: [] as number[],
    };
    setPodPayInfo(undefined);
    if (
      order.order_type === SOURCE_ORDER_TYPE.Apc &&
      isBuyCheckedStore &&
      order.enable_pod_order
    ) {
      try {
        const data = await buyJournalForm.validateFields();
        // 数量大于 0 的版本
        const targetVersions = (order.pod_versions || []).filter(
          (version) => (buyJournal.numbers[version.type] || 0) > 0,
        );
        if (!targetVersions.length) {
          messageApi.error(`${t("数量")}${t("不能为空")}`);
          return;
        }
        // 每个版本各创建一个 pod 订单
        for (const version of targetVersions) {
          const res = await createPodOrder<{
            order_id: string;
            total_amount: number;
          }>({
            apc_order_id: order.order_id,
            bill: podBillState,
            type: version.type,
            number: buyJournal.numbers[version.type],
            receive: data.receive,
          });
          if (res.code !== 0) {
            messageApi.error(res.msg);
            return;
          }
          podOrderData.orderIds.push(res.data.order_id);
          podOrderData.totalAmounts.push(res.data.total_amount);
        }
        if (way === PayWay.Offline)
          setPodPayInfo({
            order_ids: [...podOrderData.orderIds],
            total_amounts: [...podOrderData.totalAmounts],
          });
      } catch (error) {}
      if (!podOrderData.orderIds.length) return;
    }

    try {
      if (way === PayWay.Offline) {
        setModalInstance(true);
        return;
      }

      setPayLoading(true);
      let orderIds = [order.order_id];
      if (podOrderData.orderIds.length) {
        orderIds.push(...podOrderData.orderIds);
      } else {
        orderIds = [order.order_id, ...mergeOrderSelectIds];
      }

      const pay_money = [...moneyList, ...podOrderData.totalAmounts];
      const res = await commitOrder<{ transaction_id: string }>({
        bill,
        order_ids: orderIds,
        pay_money: pay_money,
      });
      setPayLoading(false);
      if (res.code === 0) {
        const transaction_id = res.data.transaction_id;
        setTransaction_id(transaction_id);
        if (way === PayWay.Card) {
          cardPayHandle(transaction_id);
        } else {
          qrPayHandle(transaction_id);
        }
      } else if (res.code === 200002) {
        location.reload();
      } else {
        messageApi.error(res.msg);
      }
    } catch (error) {
      Sentry.captureException(new Error("custom error 2"));
    }
  };

  /**
   * 协议选中
   * @param e
   */
  const check = (e) => {
    setProtocol(!protocol);
  };

  /**
   * 触发合并订单
   */
  const mergeOrder = (isMerge: boolean) => {
    if (!user) {
      toLogin();
      return;
    }
    setShowMerge(isMerge);
  };

  /**
   *
   * @param newOrder 拆分成功更新订单
   */
  const splitOrderSuccess = (newOrder) => {
    setOrder((order) => {
      return { ...order, ...newOrder };
    });
  };

  /**
   * 二维码支付。微信直连微信支付，支付宝和银联走通联。
   */
  const qrPayHandle = async (transaction_id: string) => {
    if (way === PayWay.Wx) {
      const wxRes = await wxPay<{ url: string; sandbox: boolean }>({
        transaction_id,
      });
      if (wxRes.code === 0) {
        if (wxRes.data.sandbox) {
          navigate("/result?transaction_id=" + transaction_id);
          return;
        }
        setShowWxPay(true);
        setPayUrl(wxRes.data.url);
        return;
      }
      messageApi.error(wxRes.msg);
      return;
    }
    const payType = allInPayTypeMap[way];
    if (!payType) return;
    const payRes = await allInPayQrcode<{ url: string; sandbox: boolean }>({
      transaction_id,
      pay_type: payType,
    });
    if (payRes.code === 0) {
      if (payRes.data.sandbox) {
        navigate("/result?transaction_id=" + transaction_id);
        return;
      }
      setShowWxPay(true);
      setPayUrl(payRes.data.url);
      return;
    }
    messageApi.error(payRes.msg);
  };

  /**
   * 通联银行卡支付：拿到地址和表单后由浏览器 POST 跳转
   */
  const cardPayHandle = async (transaction_id: string) => {
    const payRes = await allInPayCard<{
      url: string;
      sandbox: boolean;
      form: Record<string, string>;
    }>({
      transaction_id,
    });
    if (payRes.code === 0) {
      if (payRes.data.sandbox) {
        navigate("/result?transaction_id=" + transaction_id);
        return;
      }
      const formEl = document.createElement("form");
      formEl.method = "POST";
      formEl.action = payRes.data.url;
      formEl.acceptCharset = "UTF-8";
      Object.entries(payRes.data.form || {}).forEach(([name, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        formEl.appendChild(input);
      });
      document.body.appendChild(formEl);
      formEl.submit();
      return;
    }
    messageApi.error(payRes.msg);
  };

  /**
   * 计算总金额
   */
  useEffect(() => {
    const ids = mergeOrderSelectIds.join(",");
    const selectOrders = ids
      ? mergeOrderList.filter((item) => ids.includes(item.order_id))
      : [];
    const moneyList = [order.total_money];
    const total = {
      total_money: order.total_money,
      exp_cny_pay_money: order.exp_cny_pay_money,
      tax_money: order.tax_money,
      commission_money: order.commission_money,
      currency: order.currency,
      raw_exp_money: order.raw_exp_money,
      pay_methods: order.pay_methods,
      pay_status: order.pay_status,
      order_id: order.order_id,
      configuration: order.configuration,
    };
    for (let i = 0; i < selectOrders.length; i++) {
      const element = selectOrders[i];
      const { total_money, exp_cny_pay_money, tax_money, commission_money } =
        element;
      total.total_money = math
        .add(math.bignumber(total.total_money), math.bignumber(total_money))
        .toNumber();
      total.exp_cny_pay_money = math
        .add(
          math.bignumber(total.exp_cny_pay_money),
          math.bignumber(exp_cny_pay_money),
        )
        .toNumber();
      total.tax_money = math
        .add(math.bignumber(total.tax_money), math.bignumber(tax_money))
        .toNumber();
      total.commission_money = math
        .add(
          math.bignumber(total.commission_money),
          math.bignumber(commission_money),
        )
        .toNumber();

      moneyList.push(element.total_money);
    }

    setTotalInfo({ ...total });
    setMoneyList(moneyList);
  }, [order, mergeOrderList, mergeOrderSelectIds]);

  const getModalInstance = useCallback(
    async (order: Order, mergeOrderSelectIds: string[]) => {
      if (!user) {
        toLogin();
        return;
      }
      sentry4Action("填写发票", {
        order_id: order.order_id,
        order_type: order.order_type,
        publishing_house_name: order.publishing_house_name,
        journal_name: order.journal_name,
        article_title: order.article_title,
        cae: order.contact_author_email.split("@").join(" "),
        meeting_name: order?.meeting_name,
      });
      if (order.pay_status == 1) {
        setShowInvoiceModal(true);
      }
    },
    [bill],
  );

  return (
    <>
      {contextHolder}
      <div
        className={`${style["pc-content"]}  ${
          IS_MOBILE ? style["mobile-content"] : ""
        }`}
      >
        <div className={style.info}>
          <span className={style.order_info_wrapper}>
            <span style={{ color: "inherit" }}>{t("订单号")}</span>
            <span style={{ wordBreak: "break-all" }}>
              {order.publishing_house_order_id}
            </span>
            {order.pay_orders.length > 0 && (
              <span className={style.status}>{t("已拆分")}</span>
            )}
          </span>
          <OrderInfo order={order} splitOrderSuccess={splitOrderSuccess} />
          {order.configuration.enable_merge_order &&
            (showMerge ? (
              <AuthOtherOrder order={order} cancelMerge={mergeOrder} />
            ) : (
              order.pay_status !== 2 && (
                <Button
                  className={style["merge-order-button"]}
                  onClick={() => mergeOrder(true)}
                >
                  {t("合并支付其他订单")}
                </Button>
              )
            ))}
          {order.order_type === SOURCE_ORDER_TYPE.Apc &&
            order.enable_pod_order && (
              <BuyJournal
                order={order}
                form={buyJournalForm}
                orderBill={bill}
              />
            )}
        </div>

        <div className={style["fee-box"]}>
          <span className={style["fee-title"]}>{t("费用明细")}</span>
          <FeeDetail
            order={totalInfo}
            way={way}
            setWay={setWay}
            bill={bill}
            mergeOrderSelectIds={mergeOrderSelectIds}
            moneyList={moneyList}
          />
          {order.configuration.enable_request_bill &&
            order.pay_status !== 2 &&
            (!bill ? (
              <div
                className={style.invoice}
                onClick={() => getModalInstance(order, mergeOrderSelectIds)}
              >
                <span>{t("发票服务")}</span>
                <span>
                  <SvgIcon icon="icon-tianxie" width={14} height={14} />
                  <span>{t("申请发票")}</span>
                </span>
              </div>
            ) : (
              <div className="invoice-edit">
                <div className="btn-line">
                  <div>{t("发票服务")}</div>
                  <div className="edit">
                    <span
                      className="btn"
                      onClick={() =>
                        getModalInstance(order, mergeOrderSelectIds)
                      }
                    >
                      <SvgIcon icon="icon-tianxie" width={14} height={14} />
                      {t("修改")}
                    </span>
                    <Divider type="vertical" />
                    <span
                      className="btn"
                      onClick={() => {
                        Modal.confirm({
                          title: t("确认"),
                          content: t("是否确认删除 ?"),
                          okText: t("确认"),
                          cancelText: t("取消"),
                          onOk: () => {
                            setBill(null);
                          },
                        });
                      }}
                    >
                      <SvgIcon icon="icon-shanchu" width={14} height={14} />
                      {t("清除")}
                    </span>
                  </div>
                </div>
                <Divider className="h-divider" />
                <div className="text-line">
                  <div>
                    <span className="title">{t("发票类型")}</span>
                    <span className="value">
                      {t(InvoiceTypeMap[bill.bill_type])}
                      {/* {bill.bill_type === 1
                        ? t("电子增值税发票")
                        : t("增值税专用发票")} */}
                    </span>
                  </div>
                  <div>
                    <span className="title">{t("抬头名称")}</span>
                    <span className="value">{bill.title_title}</span>
                  </div>
                </div>
              </div>
            ))}
          <Button
            type="primary"
            className={`confirm-pay ${
              order.pay_status == 2 && style["element-disable"]
            }`}
            onClick={() => confirmPay()}
            // loading={payLoading}
            disabled={order.pay_status !== 1}
          >
            {order.pay_status == 1
              ? t("确认支付")
              : order.pay_status == 2
                ? t("订单已支付")
                : t("订单已关闭或已取消")}
          </Button>
          <Checkbox
            className="protocol"
            checked={protocol}
            onChange={check}
            disabled={order.pay_status === 2}
          >
            <span> {t("我已阅读并同意 PubLink")} </span>
            <a className="light" href="/agreement/use.html" target="_blank">
              {t("使用协议")}
            </a>
            <span> {t("和")} </span>
            <a className="light" href="/agreement/privacy.html" target="_blank">
              {t("隐私协议")}
            </a>
          </Checkbox>
        </div>
      </div>
      {modalInstance}
      {showWxPay && (
        <WxPay
          way={way}
          value={{
            url: payUrl,
            money: totalInfo.total_money,
            transaction_id: transaction_id,
          }}
          confirm={() => {
            setShowWxPay(false);
          }}
          cancel={() => {
            setShowWxPay(false);
          }}
        ></WxPay>
      )}

      {showInvoiceModal && (
        <InvoiceModal
          hiddenModal={() => setShowInvoiceModal(false)}
          invoiceInfo={defaultBill}
          getInvoiceInfo={(info) => {
            // let filteredObj = { ...bill, ...info };
            // if (filteredObj.bill_type === 1) {
            //   filteredObj = Object.keys(filteredObj).reduce(
            //     (acc, key) => {
            //       if (!key.includes("address") || key === "title_address") {
            //         acc[key] = filteredObj[key];
            //       }
            //       return acc;
            //     },
            //     {} as InvoiceInfo & InvoiceAddress
            //   );
            // }
            setBill(info);
          }}
          orderIds={[order.order_id, ...mergeOrderSelectIds]}
          additionalParams={{ orderType: order.order_type }}
        />
      )}
      {modalInstance && (
        <OfflineModal
          hiddenModal={() => setModalInstance(false)}
          actionParams={{
            bill,
            order_ids: podPayInfo
              ? [order.order_id, ...mergeOrderSelectIds, ...podPayInfo.order_ids]
              : [order.order_id, ...mergeOrderSelectIds],
            pay_money: podPayInfo
              ? moneyList.concat(podPayInfo.total_amounts)
              : moneyList,
          }}
        />
      )}
    </>
  );
};

export default PcInfo;
